<template>
  <div class="admin">
    <!-- The head. The admin screens carry noindex from the resolver on the server,
         so it is in the first response and does not depend on this component
         running. It used to be injected by hand into `document.head` from
         `created()`, which is a tag Inertia's Head does not own and never removes:
         the noindex stayed in the document after navigating from the console to a
         public page, and every public page visited in the rest of that session was
         silently excluded from the index. -->
    <page-meta />
    <aside class="sidebar">
      <Link :href="route('admin.home')" class="sidebar__brand">Veximus</Link>

      <nav class="sidebar__nav">
        <div class="sidebar__group">
          <div class="sidebar__label">Inbox</div>
          <Link
            :href="route('admin.inbox.index')"
            class="sidebar__item"
            :class="{ 'is-active': current === 'threads' }"
          >Threads</Link>
          <Link
            :href="route('admin.inbox.leads')"
            class="sidebar__item"
            :class="{ 'is-active': current === 'leads' }"
          >Leads</Link>
          <Link
            :href="route('admin.inbox.appointments')"
            class="sidebar__item"
            :class="{ 'is-active': current === 'appointments' }"
          >Appointments</Link>
        </div>

        <div class="sidebar__group">
          <div class="sidebar__label">Website</div>
          <Link
            :href="route('admin.chat.threads')"
            class="sidebar__item"
            :class="{ 'is-active': current === 'chat-threads' }"
          >Threads</Link>
          <Link
            :href="route('admin.chat.leads')"
            class="sidebar__item"
            :class="{ 'is-active': current === 'chat-leads' }"
          >Leads</Link>
          <Link
            :href="route('admin.chat.appointments')"
            class="sidebar__item"
            :class="{ 'is-active': current === 'chat-appointments' }"
          >Appointments</Link>
        </div>

        <div class="sidebar__group">
          <div class="sidebar__label">Referrals</div>
          <Link
            :href="route('admin.referrals.index')"
            class="sidebar__item"
            :class="{ 'is-active': current === 'referrals' }"
          >Codes</Link>
        </div>

        <div class="sidebar__group">
          <div class="sidebar__label">Invoices</div>
          <Link
            :href="route('admin.invoices.index')"
            class="sidebar__item"
            :class="{ 'is-active': current === 'invoices' }"
          >Invoices</Link>
          <Link
            :href="route('admin.invoices.clients')"
            class="sidebar__item"
            :class="{ 'is-active': current === 'clients' }"
          >Clients</Link>
          <Link
            :href="route('admin.invoices.projects')"
            class="sidebar__item"
            :class="{ 'is-active': current === 'projects' }"
          >Projects</Link>
          <Link
            :href="route('admin.invoices.subscriptions')"
            class="sidebar__item"
            :class="{ 'is-active': current === 'subscriptions' }"
          >Subscriptions</Link>
        </div>
      </nav>

      <div class="sidebar__footer">
        <div class="sidebar__email" :title="userEmail">{{ userEmail }}</div>
        <button type="button" class="sidebar__logout" :disabled="loggingOut" @click="logout">
          Log out
        </button>
      </div>
    </aside>

    <main class="admin__main">
      <slot />
    </main>
  </div>
</template>

<style lang="scss" scoped>
.admin {
  background: var(--admin-bg);
  display: flex;
  height: 100vh;
  height: 100dvh;
  overflow: hidden;
}

.sidebar {
  background: var(--admin-surface);
  border-right: 1px solid var(--admin-border);
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  width: 16rem;
}

.sidebar__brand {
  border-bottom: 1px solid var(--admin-border);
  color: var(--admin-text);
  display: block;
  font-size: 0.95rem;
  font-weight: 600;
  letter-spacing: -0.02em;
  margin-bottom: 0.5rem;
  padding: 1rem 1rem 0.9rem;
  text-decoration: none;
}

.sidebar__nav {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 1.15rem;
  min-height: 0;
  overflow: auto;
  padding: 0.25rem 0.75rem 1rem;
}

.sidebar__group {
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
}

.sidebar__label {
  color: var(--admin-muted);
  font-size: 0.7rem;
  font-weight: 500;
  letter-spacing: 0.04em;
  padding: 0.35rem 0.6rem;
  text-transform: uppercase;
}

.sidebar__item {
  border-radius: calc(var(--admin-radius) - 2px);
  color: var(--admin-muted);
  font-size: 0.875rem;
  font-weight: 400;
  line-height: 1;
  padding: 0.5rem 0.6rem;
  text-decoration: none;
  transition: background 0.12s ease, color 0.12s ease;

  &:hover {
    background: var(--admin-accent);
    color: var(--admin-text);
  }

  &.is-active {
    background: var(--admin-accent);
    color: var(--admin-text);
    font-weight: 500;
  }
}

.sidebar__footer {
  border-top: 1px solid var(--admin-border);
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
  padding: 0.85rem 0.75rem;
}

.sidebar__email {
  color: var(--admin-muted);
  font-size: 0.75rem;
  overflow: hidden;
  padding: 0 0.35rem;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.sidebar__logout {
  background: var(--admin-surface);
  border: 1px solid var(--admin-border);
  border-radius: calc(var(--admin-radius) - 2px);
  color: var(--admin-text);
  cursor: pointer;
  font: inherit;
  font-size: 0.8rem;
  font-weight: 500;
  padding: 0.4rem 0.65rem;
  text-align: left;

  &:hover:not(:disabled) {
    background: var(--admin-accent);
  }

  &:disabled {
    cursor: default;
    opacity: 0.6;
  }
}

.admin__main {
  display: flex;
  flex: 1;
  flex-direction: column;
  min-height: 0;
  min-width: 0;
  overflow: hidden;
  padding: 1rem;
}
</style>

<script>
import { Link } from '@inertiajs/inertia-vue3';
import PageMeta from '@/Components/PageMeta.vue';

export default {
  components: {
    Link,
    PageMeta
  },

  props: {
    current: {
      type: String,
      required: true
    }
  },

  data() {
    return {
      loggingOut: false
    };
  },

  computed: {
    userEmail() {
      const auth = this.$page.props.auth || {};
      return (auth.user && auth.user.email) || '';
    }
  },

  methods: {
    logout() {
      if (this.loggingOut) return;
      this.loggingOut = true;
      this.$inertia.post(this.route('logout'), {}, {
        onFinish: () => {
          this.loggingOut = false;
        }
      });
    }
  },

  created() {
    this._prevHtmlOverflow = document.documentElement.style.overflow;
    this._prevBodyOverflow = document.body.style.overflow;
    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';
  },

  beforeUnmount() {
    document.documentElement.style.overflow = this._prevHtmlOverflow || '';
    document.body.style.overflow = this._prevBodyOverflow || '';
  }
};
</script>
